import { isTouchDevice } from "@/utils/getters";

import Variants from "./Variants";

export type IThemeProps<P = {}> = P & { theme: ITheme };

// Font Size
// The font concept has to be improved in future
// Duplicate values can be overwritten at tens digit h3x0 (e.g. h310)
export const typography: ThemeInterface.ITypography = {
  h100: "10px",
  h200: "11px",
  h300: "12px",
  h310: "12px",
  h320: "12px",
  h330: "12px",
  h400: "14px",
  h410: "14px",
  h420: "14px",
  h500: "16px",
  h600: "18px",
  h610: "18px",
  h700: "22px",
  h710: "22px",
  h720: "22px",
  h800: "24px",
  h900: "52px",
};

// Element stack orders
export const zIndex = {
  h100: 1,
  h200: 9,
  h300: 10,
  h500: 1000,
  h900: 2147483647,
};

// Designer specific brand sizes
export const dimensions = {
  maxInteractionWidth: "580px",
  minInteractionHeight: "470px",
  isTouch: isTouchDevice(),
};

// Customized logical definitions
export const thesis: ThemeInterface.IThesis = {
  hasBoxShadow: true,
  hasButtonInvert: false,
  hasIntroBackground: true,
  hasTransparency: true,
  hasPrimaryPrio: false,
  hasExtendedThumbsHeader: true,
  hasExtraThumbsPadding: false,
  hasThumbsHoverEffect: true,
};

// Media queries specs
export const breakpoints = {
  mobile: `(min-width: 0px) and (max-width: 480px)`,
  mobileLandscape: `(min-width: 481px) and (max-width: 767px)`,
  tablet: `(min-width: 768px) and (max-width: 1024px)`,
  tabletLandscape: `(min-width: 768px) and (max-width: 1024px) and (orientation: landscape)`,
  desktop: `(min-width: 1025px) and (max-width: 3840px)`,
};

// Element shadows
export const shadows = {
  s100: `none`,
  s200: `0 3px 6px 5px rgba(0, 0, 0, 0.5)`,
  s300: `0 5px 20px 0 rgba(0, 0, 0, 0.1)`,
  s400: `0px 8px 24px rgba(0, 0, 0, 0.1)`,
};

export enum ThemeName {
  aubi = "aubi",
  bild = "bild",
  bild_brandstory = "bild_brandstory",
  bild_fitbook = "bild_fitbook",
  bild_myhomebook = "bild_myhomebook",
  bild_productstory = "bild_productstory",
  bild_stylebook = "bild_stylebook",
  bild_techbook = "bild_techbook",
  bild_travelbook = "bild_travelbook",
  bz = "bz",
  cobi = "cobi",
  default = "default",
  icon = "icon",
  mediapioneer = "mediapioneer",
  metalhammer = "metalhammer",
  musikexpress = "musikexpress",
  rollingstone = "rollingstone",
  spobi = "spobi",
  welt = "welt",
  welt_brandstory = "welt_brandstory",
  welt_contrast = "welt_contrast",
  welt_productstory = "welt_productstory",
}

const themeRegistry: Record<ThemeName, ITheme> = {
  // Generic Styling
  default: {
    themeName: ThemeName.default,
    globals: {
      fontFamily: `Ubuntu, sans-serif`,
      fontFamily200: `Ubuntu, sans-serif`,
      fontFamily400: `Ubuntu Condensed, sans-serif`,
      fontFamily600: `Ubuntu, sans-serif`,
      fontFamilyExtra: `Ubuntu, sans-serif`,
      fontFamilyExtra2: `Ubuntu, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.default,
  },

  // COBI Styling
  cobi: {
    themeName: ThemeName.cobi,
    globals: {
      fontFamily: `"Open Sans", Arial, sans-serif`,
      fontFamily200: `"Open Sans Light", Arial, sans-serif`,
      fontFamily400: `"Open Sans", Arial, sans-serif`,
      fontFamily600: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra2: `"Open Sans", Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.cobi,
  },

  // AUBI Styling
  aubi: {
    themeName: ThemeName.aubi,
    globals: {
      fontFamily: `"Open Sans", Arial, sans-serif`,
      fontFamily200: `"Open Sans Light", Arial, sans-serif`,
      fontFamily400: `"Open Sans", Arial, sans-serif`,
      fontFamily600: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra2: `"Open Sans", Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.aubi,
  },

  // spobi Styling (Sport Bild)
  spobi: {
    themeName: ThemeName.spobi,
    globals: {
      fontFamily: `"Open Sans", Arial, sans-serif`,
      fontFamily200: `"Open Sans", Arial, sans-serif`,
      fontFamily400: `"Open Sans", Arial, sans-serif`,
      fontFamily600: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra: `"AntennaCondBold", "Open Sans", Arial, sans-serif`,
      fontFamilyExtra2: `"AntennaCondBold", "Open Sans", Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.spobi,
  },

  // BILD Styling
  bild: {
    themeName: ThemeName.bild,
    globals: {
      fontFamily: `"GothamXNarrow", Helvetica, Tahoma, sans-serif`,
      fontFamily200: `"GothamXNarrow", Helvetica, Tahoma, sans-serif`,
      fontFamily400: `"GothamXNarrow", Helvetica, Tahoma, sans-serif`,
      fontFamily600: `"Gotham", Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra: `"Gotham", Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra2: `"GothamCondensed", Helvetica, Tahoma, sans-serif`,
      fontWeightBold: 900,
      ...dimensions,
    },
    breakpoints,
    typography: {
      ...typography,
      h100: "12px",
      h200: "14px",
      h310: "21px",
      h320: "18px",
      h330: "14px",
      h400: "14px",
      h410: "15px",
      h420: "16px",
      h610: "20px",
      h710: "36px",
      h720: "16px",
    },
    thesis: {
      ...thesis,
      hasBoxShadow: true,
      hasTransparency: false,
      hasPrimaryPrio: true,
      hasExtendedThumbsHeader: false,
    },
    shadows: {
      ...shadows,
      s100: `0 3px 6px rgba(0, 0, 0, 0.29)`,
      s200: `none`,
    },
    zIndex,
    ...Variants.bild,
  },

  // BZ Styling
  bz: {
    themeName: ThemeName.bz,
    globals: {
      fontFamily: `"GothamXNarrow", Helvetica, Tahoma, sans-serif`,
      fontFamily200: `"GothamXNarrow", Helvetica, Tahoma, sans-serif`,
      fontFamily400: `"GothamXNarrow", Helvetica, Tahoma, sans-serif`,
      fontFamily600: `"Gotham", Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra: `"Gotham", Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra2: `"GothamCondensed", Helvetica, Tahoma, sans-serif`,
      fontWeightBold: 900,
      ...dimensions,
    },
    breakpoints,
    typography: Object.assign({}, typography, {
      h100: "12px",
      h200: "14px",
      h310: "21px",
      h320: "18px",
      h330: "14px",
      h400: "14px",
      h410: "15px",
      h420: "16px",
      h610: "20px",
      h710: "36px",
      h720: "16px",
    }),
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: true,
      hasTransparency: false,
      hasPrimaryPrio: true,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `0 3px 6px rgba(0, 0, 0, 0.29)`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.bild,
  },

  // Bild-Fitbook Styling (also for BRANDSTUDIO)
  bild_fitbook: {
    themeName: ThemeName.bild_fitbook,
    globals: {
      fontFamily: `"Open Sans", Arial, sans-serif`,
      fontFamily200: `"Open Sans Light", Arial, sans-serif`,
      fontFamily400: `"Open Sans", Arial, sans-serif`,
      fontFamily600: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra2: `"Open Sans", Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.bild_fitbook,
  },

  // Bild-Stylebook Styling (also for BRANDSTUDIO)
  bild_stylebook: {
    themeName: ThemeName.bild_stylebook,
    globals: {
      fontFamily: `"Open Sans", Arial, sans-serif`,
      fontFamily200: `"Open Sans Light", Arial, sans-serif`,
      fontFamily400: `"Open Sans", Arial, sans-serif`,
      fontFamily600: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra2: `"Open Sans", Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.bild_stylebook,
  },

  // Bild-Techbook Styling (also for BRANDSTUDIO)
  bild_techbook: {
    themeName: ThemeName.bild_techbook,
    globals: {
      fontFamily: `"Open Sans", Arial, sans-serif`,
      fontFamily200: `"Open Sans Light", Arial, sans-serif`,
      fontFamily400: `"Open Sans", Arial, sans-serif`,
      fontFamily600: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra2: `"Open Sans", Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.bild_techbook,
  },

  // Bild-Travelbook Styling (also for BRANDSTUDIO)
  bild_travelbook: {
    themeName: ThemeName.bild_travelbook,
    globals: {
      fontFamily: `"Open Sans", Arial, sans-serif`,
      fontFamily200: `"Open Sans Light", Arial, sans-serif`,
      fontFamily400: `"Open Sans", Arial, sans-serif`,
      fontFamily600: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra2: `"Open Sans", Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.bild_travelbook,
  },

  // Bild-MyHomebook Styling (also for BRANDSTUDIO)
  bild_myhomebook: {
    themeName: ThemeName.bild_myhomebook,
    globals: {
      fontFamily: `"Open Sans", Arial, sans-serif`,
      fontFamily200: `"Open Sans Light", Arial, sans-serif`,
      fontFamily400: `"Open Sans", Arial, sans-serif`,
      fontFamily600: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra: `"Open Sans", Arial, sans-serif`,
      fontFamilyExtra2: `"Open Sans", Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows,
    zIndex,
    ...Variants.bild_myhomebook,
  },

  // BRANDSTUDIO Bild Brand Story Styling
  bild_brandstory: {
    themeName: ThemeName.bild_brandstory,
    globals: {
      fontFamily: `"GothamXNarrow", Helvetica, Tahoma, sans-serif`,
      fontFamily200: `"GothamXNarrow", Helvetica, Tahoma, sans-serif`,
      fontFamily400: `"GothamXNarrow", Helvetica, Tahoma, sans-serif`,
      fontFamily600: `"Gotham", Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra: `"Gotham", Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra2: `"GothamCondensed", Helvetica, Tahoma, sans-serif`,
      fontWeightBold: 900,
      ...dimensions,
    },
    breakpoints,
    typography: Object.assign({}, typography, {
      h100: "12px",
      h200: "14px",
      h310: "21px",
      h320: "18px",
      h410: "15px",
      h610: "20px",
      h710: "36px",
    }),
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: true,
      hasTransparency: false,
      hasPrimaryPrio: true,
      hasExtendedThumbsHeader: false,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `0 3px 6px rgba(0, 0, 0, 0.29)`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.bild,
  },

  // BRANDSTUDIO Bild Product Story Styling
  bild_productstory: {
    themeName: ThemeName.bild_productstory,
    globals: {
      fontFamily: `"LucidaGrande", Lucida Grande,Lucida Sans Unicode,Lucida Sans,Geneva,Verdana,sans-serif`,
      fontFamily200: `"LucidaGrande", Lucida Grande,Lucida Sans Unicode,Lucida Sans,Geneva,Verdana,sans-serif`,
      fontFamily400: `"LucidaGrande", Lucida Grande,Lucida Sans Unicode,Lucida Sans,Geneva,Verdana,sans-serif`,
      fontFamily600: `"LucidaGrande", Lucida Grande,Lucida Sans Unicode,Lucida Sans,Geneva,Verdana,sans-serif`,
      fontFamilyExtra: `"LucidaGrande", Lucida Grande,Lucida Sans Unicode,Lucida Sans,Geneva,Verdana,sans-serif`,
      fontFamilyExtra2: `"LucidaGrande", Lucida Grande,Lucida Sans Unicode,Lucida Sans,Geneva,Verdana,sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
    },
    breakpoints,
    typography: Object.assign({}, typography, {
      h100: "12px",
      h200: "14px",
    }),
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: true,
      hasTransparency: false,
      hasPrimaryPrio: true,
      hasExtendedThumbsHeader: false,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `0 3px 6px rgba(0, 0, 0, 0.29)`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.bild,
  },

  // WELT Styling
  welt: {
    themeName: ThemeName.welt,
    globals: {
      fontFamily: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamily200: `ffmark Condensed, "Helvetica Neue", Helvetica,Arial, sans-serif`,
      fontFamily400: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamily600: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamilyExtra: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamilyExtra2: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
      maxInteractionWidth: "620px",
    },
    breakpoints,
    typography: Object.assign({}, typography, {
      h700: "18px",
      h720: "20px",
    }),
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: false,
      hasThumbsHoverEffect: false,
      hasButtonInvert: true,
      hasExtendedThumbsHeader: false,
      hasExtraThumbsPadding: true,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `none`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.welt,
  },

  welt_contrast: {
    themeName: ThemeName.welt_contrast,
    globals: {
      fontFamily: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamily200: `ffmark Condensed, "Helvetica Neue", Helvetica,Arial, sans-serif`,
      fontFamily400: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamily600: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamilyExtra: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamilyExtra2: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
      maxInteractionWidth: "620px",
    },
    breakpoints,
    typography: Object.assign({}, typography, {
      h700: "18px",
      h720: "20px",
    }),
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: false,
      hasThumbsHoverEffect: false,
      hasButtonInvert: true,
      hasExtendedThumbsHeader: false,
      hasExtraThumbsPadding: true,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `none`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.welt_contrast,
  },

  // BRANDSTUDIO WELT Brandstory Styling
  welt_brandstory: {
    themeName: ThemeName.welt_brandstory,
    globals: {
      fontFamily: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamily200: `ffmark Condensed, "Helvetica Neue", Helvetica,Arial, sans-serif`,
      fontFamily400: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamily600: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamilyExtra: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamilyExtra2: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
      maxInteractionWidth: "620px",
    },
    breakpoints,
    typography: Object.assign({}, typography, {
      h700: "18px",
    }),
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: false,
      hasThumbsHoverEffect: false,
      hasButtonInvert: true,
      hasExtendedThumbsHeader: false,
      hasExtraThumbsPadding: true,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `none`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.welt,
  },

  // BRANDSTUDIO WELT Brandstory Styling
  welt_productstory: {
    themeName: ThemeName.welt_productstory,
    globals: {
      fontFamily: `"Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamily200: `"Helvetica Neue", Helvetica,Arial, sans-serif`,
      fontFamily400: `"Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamily600: `"Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamilyExtra: `"Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamilyExtra2: `"Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
      maxInteractionWidth: "620px",
    },
    breakpoints,
    typography: Object.assign({}, typography, {
      h700: "18px",
    }),
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: false,
      hasThumbsHoverEffect: false,
      hasButtonInvert: true,
      hasExtendedThumbsHeader: false,
      hasExtraThumbsPadding: true,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `none`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.welt,
  },

  // ICON Styling
  icon: {
    themeName: ThemeName.icon,
    globals: {
      fontFamily: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamily200: `ffmark Condensed, "Helvetica Neue", Helvetica,Arial, sans-serif`,
      fontFamily400: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamily600: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamilyExtra: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontFamilyExtra2: `ffmark, "Helvetica Neue", Helvetica, Arial, sans-serif`,
      fontWeightBold: 600,
      ...dimensions,
      maxInteractionWidth: "620px",
    },
    breakpoints,
    typography: Object.assign({}, typography, {
      h700: "18px",
    }),
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: false,
      hasThumbsHoverEffect: false,
      hasButtonInvert: true,
      hasExtendedThumbsHeader: false,
      hasExtraThumbsPadding: true,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `none`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.icon,
  },

  // MEDIA_PIONEER Styling
  mediapioneer: {
    themeName: ThemeName.mediapioneer,
    globals: {
      fontFamily: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamily200: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamily400: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamily600: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra2: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontWeightBold: 700,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis,
    shadows: Object.assign({}, shadows, {
      s100: `none`,
      s200: `0px 0px 4px 0px rgba(93, 45, 188, 0.5)`,
    }),
    zIndex,
    ...Variants.mediapioneer,
  },

  // MUSIKEXPRESS Styling
  musikexpress: {
    themeName: ThemeName.musikexpress,
    globals: {
      fontFamily: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamily200: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamily400: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamily600: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra2: `Poppins, Helvetica, Tahoma, sans-serif`,
      fontWeightBold: 700,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: true,
      hasButtonInvert: false,
      hasPrimaryPrio: true,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `none`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.musikexpress,
  },

  rollingstone: {
    themeName: ThemeName.rollingstone,
    globals: {
      fontFamily: `Tahoma, Helvetica, sans-serif`,
      fontFamily200: `Tahoma, Helvetica, sans-serif`,
      fontFamily400: `Tahoma, Helvetica, sans-serif`,
      fontFamily600: `Tahoma, Helvetica, sans-serif`,
      fontFamilyExtra: `Tahoma, Helvetica, sans-serif`,
      fontFamilyExtra2: `Tahoma, Helvetica, sans-serif`,
      fontWeightBold: 700,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: true,
      hasButtonInvert: false,
      hasPrimaryPrio: true,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `none`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.rollingstone,
  },

  metalhammer: {
    themeName: ThemeName.metalhammer,
    globals: {
      fontFamily: `Arvo, Helvetica, Tahoma, sans-serif`,
      fontFamily200: `Arvo, Helvetica, Tahoma, sans-serif`,
      fontFamily400: `Arvo, Helvetica, Tahoma, sans-serif`,
      fontFamily600: `Arvo, Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra: `Arvo, Helvetica, Tahoma, sans-serif`,
      fontFamilyExtra2: `Arvo, Helvetica, Tahoma, sans-serif`,
      fontWeightBold: 700,
      ...dimensions,
    },
    breakpoints,
    typography,
    thesis: Object.assign({}, thesis, {
      hasBoxShadow: true,
      hasButtonInvert: false,
      hasPrimaryPrio: true,
    }),
    shadows: Object.assign({}, shadows, {
      s100: `none`,
      s200: `none`,
    }),
    zIndex,
    ...Variants.metalhammer,
  },
};

export { themeRegistry };
